import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { ApiContext } from "../../../context/MyContext";
import { useUtils } from "../../../context/UtilsContext";
import { fetchEmployeeHierarchy, fetchEmployees } from "../../../service/employeeService";
import {
  fetchBookingRecords,
  fetchCoachShifts,
  fetchShiftCallTypes,
} from "../../../service/bookingService";
import {
  SHIFT_CALL_TYPES_KEY,
  coachShiftsCache,
  employeeHierarchyCache,
  employeeListCache,
  serializeCoachShiftsKey,
  serializeEmployeeHierarchyKey,
  shiftCallTypesCache,
} from "../../../utils/listCache/caches";
import { resolveBookingEventTypeId } from "./bookingEventType";
import { isActiveBooking } from "./bookingStatus";
import {
  COACH_ROLE_TITLES,
  getCoachDepartmentTitles,
  toCoachRows,
  toTenantCoachRows,
} from "./coachRoster";
import {
  buildWeek,
  callTypesForDepartment,
  countDaySlots,
  shiftsOnDate,
  utcShiftsToLocal,
} from "./slotGrid";

/**
 * Data layer for a coach roster. **Two scopes, two endpoints**, because the two
 * audiences hold their authority for different reasons.
 *
 * - `scope: "team"` — a Fitness/Nutritionist **Manager**'s own team, from
 *   `GET /fetch-employee-hierarchy`. The endpoint takes no parameters because
 *   the caller is the subject, and it has already applied every liveness rule
 *   this hook used to reproduce by hand. **Direct reports only**, matching the
 *   backend: `517da9b` deleted `may_act_for`'s depth-2 branch, so a coach
 *   reached through another manager is no longer the caller's to publish for.
 *   Narrowed further to the caller's own coach department, which is a product
 *   rule the API does not apply — a Fitness Manager's line may reach a
 *   Nutritionist Manager and `may_act_for` would allow it, but "My team" means
 *   their team. See `getCoachDepartmentTitles`.
 * - `scope: "all"` — every coach in the tenant, for an **executive**, from
 *   `GET /fetch-employees`. The reporting tree cannot answer this one: an
 *   executive who manages nobody has an empty tree, while `may_act_for` checks
 *   `is_executive` first and grants them every calendar. See
 *   `toTenantCoachRows` for why the shaping is borrowed from the Employees
 *   page rather than rewritten.
 *
 * Both then fill the two count columns with three requests for the whole table
 * rather than any per coach:
 * - **Slots this week** — `/fetch-coach-shift` with no `employee_id` (every
 *   coach's shifts, token-only) and `/fetch-shift-call-type` (every
 *   department's), put together with the same generator the View slots dialog
 *   uses: regular-call slots across the coach's seven days (`countDaySlots`).
 *   Shifts repeat weekly, so this is the same number every week.
 * - **Calls today** — `/fetch-booking-records` for today, unnarrowed: its scope
 *   predicate already returns the caller's own calendar plus their direct
 *   reports' (everything, for an executive). Cancelled calls do not count.
 *
 * **No longer a probe.** This used to be called at the top of `BookingsPage`
 * by everybody, because the reporting tree was the only thing that could tell
 * whether a caller stood above a coach manager. That case is gone — it is
 * executive standing now, which is decided synchronously from context — so the
 * hook is called only where a roster is actually rendered, and nobody waits on
 * a round trip to find out which screen they get.
 *
 * @param {{ scope?: "team"|"all", now?: Date }} [options] - `now` is injectable
 *   so the week window is deterministic in tests.
 */
// `/fetch-employees` takes no parameters and applies no ownership gate, so
// every caller in the tenant gets the identical payload — there is nothing to
// key the cache by. See `employeeListCache`, whose comment contrasts this with
// the caller-scoped hierarchy cache beside it.
const ALL_EMPLOYEES_KEY = "all";

export function useCoachRoster({ scope = "team", now } = {}) {
  const { get, employeeData, departmentsWithRoles } = useContext(ApiContext);
  const { sessionUtils } = useUtils();

  const [rows, setRows] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reloadToken, setReloadToken] = useState(0);

  // Frozen for the lifetime of the screen unless explicitly passed: re-deriving
  // `new Date()` on every render would rebuild the week window each time and
  // retrigger the fetch forever.
  const fallbackNow = useRef(new Date());
  const effectiveNow = now || fallbackNow.current;

  const myEmployeeId = employeeData?.data?.employee_id || null;

  // A department's manager gets their department, not everyone their reporting
  // line happens to reach. Empty for an executive, whose table is every coach
  // department by definition.
  const myDepartments = useMemo(
    () => getCoachDepartmentTitles(employeeData, departmentsWithRoles),
    [employeeData, departmentsWithRoles]
  );
  // The effect depends on the *contents*, not the array: both context values
  // are rebuilt on any context change, and depending on them directly would
  // refetch the whole tree for a scope that has not moved.
  const departmentsKey = myDepartments.join("|");
  const allCoaches = scope === "all";

  const eventTypeId = useMemo(
    () => resolveBookingEventTypeId(sessionUtils),
    [sessionUtils]
  );

  const configured = Boolean(myEmployeeId);

  // Set by `refresh` and consumed once by the effect below, so an explicit retry
  // bypasses the cache while an ordinary re-run (a dependency changing, a
  // remount) still gets to use it.
  const forceRef = useRef(false);
  const refresh = useCallback(() => {
    forceRef.current = true;
    setReloadToken((token) => token + 1);
  }, []);

  useEffect(() => {
    if (!configured) {
      setRows(null);
      setLoading(false);
      return undefined;
    }

    let cancelled = false;
    const force = forceRef.current;
    forceRef.current = false;
    setLoading(true);
    setError(null);

    const dayStart = new Date(
      effectiveNow.getFullYear(),
      effectiveNow.getMonth(),
      effectiveNow.getDate()
    );
    const dayEnd = new Date(dayStart.getTime() + 24 * 60 * 60 * 1000 - 1);
    const thisWeek = buildWeek(effectiveNow, effectiveNow);

    const run = async () => {
      try {
        // Both lists are cached across navigations, and `refresh()` bumps
        // `reloadToken` to re-run this effect with `force` set — so a manager
        // link or a role created on /emp is one button away rather than a
        // reload away.
        let coaches;
        if (allCoaches) {
          // Every coach in the tenant. Not the reporting tree: an executive's
          // authority is `is_executive`, not a line of managers, so an
          // executive who manages nobody must still get the whole table.
          const cached = force ? undefined : employeeListCache.get(ALL_EMPLOYEES_KEY);
          let employees;
          if (cached) {
            employees = cached;
          } else {
            const payload = await fetchEmployees(get);
            employees = Array.isArray(payload?.employees) ? payload.employees : [];
            employeeListCache.set(ALL_EMPLOYEES_KEY, employees);
          }
          coaches = toTenantCoachRows({
            employees,
            departmentsWithRoles,
            callerEmployeeId: myEmployeeId,
          });
        } else {
          const cacheKey = serializeEmployeeHierarchyKey(myEmployeeId);
          const cached = force ? undefined : employeeHierarchyCache.get(cacheKey);
          let reports;
          if (cached) {
            reports = cached;
          } else {
            try {
              const payload = await fetchEmployeeHierarchy(get);
              reports = Array.isArray(payload?.reports) ? payload.reports : [];
            } catch (err) {
              // A 403 is the endpoint saying "you have no employee record in
              // this application" — an answer, not a failure. Surfacing it as a
              // retry banner would tell someone to keep trying something that
              // will never work; an empty roster is what it actually means.
              if (err?.status === 403) reports = [];
              else throw err;
            }
            employeeHierarchyCache.set(cacheKey, reports);
          }

          // A manager's own team: their department, their coaches, and only the
          // ones assigned to them rather than to a peer manager.
          // `directReportsOnly` is unconditional now — the backend deleted the
          // depth-2 grant, so a coach reached through another manager would be
          // a row that 403s when you try to publish for them.
          coaches = toCoachRows({
            reports,
            callerEmployeeId: myEmployeeId,
            departments: myDepartments,
            roleTitles: COACH_ROLE_TITLES,
            directReportsOnly: true,
          });
        }

        if (cancelled) return;

        // Counts are a nicety; a failure behind them must not cost the roster,
        // so each of the three reads degrades to "no number" on its own.
        let shiftRows = [];
        let callTypeRows = [];
        let todaysBookings = [];
        if (eventTypeId && coaches.length > 0) {
          const cachedList = async (cache, key, load) => {
            const hit = force ? undefined : cache.get(key);
            if (hit) return hit;
            const rows = await load();
            cache.set(key, rows);
            return rows;
          };
          [shiftRows, callTypeRows, todaysBookings] = await Promise.all([
            cachedList(coachShiftsCache, serializeCoachShiftsKey(null), () =>
              fetchCoachShifts(get, { eventTypeId }).then((payload) =>
                Array.isArray(payload?.coach_shifts) ? payload.coach_shifts : []
              )
            ).catch(() => []),
            cachedList(shiftCallTypesCache, SHIFT_CALL_TYPES_KEY, () =>
              fetchShiftCallTypes(get, { eventTypeId }).then((payload) =>
                Array.isArray(payload?.shift_call_types) ? payload.shift_call_types : []
              )
            ).catch(() => []),
            fetchBookingRecords(get, {
              eventTypeId,
              fromDate: dayStart.toISOString(),
              toDate: dayEnd.toISOString(),
            })
              .then((payload) =>
                Array.isArray(payload?.booking_records) ? payload.booking_records : []
              )
              .catch(() => []),
          ]);
        }

        // Grouped once rather than filtered per row: both lists span the tenant.
        const shiftsByCoach = new Map();
        shiftRows.forEach((row) => {
          const id = row?.employee_id == null ? null : String(row.employee_id);
          if (!id) return;
          if (!shiftsByCoach.has(id)) shiftsByCoach.set(id, []);
          shiftsByCoach.get(id).push(row);
        });
        const callsByCoach = new Map();
        todaysBookings.filter(isActiveBooking).forEach((booking) => {
          const id = booking?.coach_id == null ? null : String(booking.coach_id);
          if (!id) return;
          callsByCoach.set(id, (callsByCoach.get(id) || 0) + 1);
        });

        coaches.forEach((coach) => {
          const { tiers } = callTypesForDepartment(callTypeRows, coach.departmentTitle);
          const local = utcShiftsToLocal(shiftsByCoach.get(coach.employeeId) || []);
          // This week's actual dates, since a shift is in effect only between
          // its effective_from and effective_to (`be3e423`).
          const week = thisWeek.map((day) =>
            countDaySlots(shiftsOnDate(local, day.date), tiers, undefined, day.date)
          );
          // No call types for the department means there is no number, not zero.
          coach.slotsThisWeek = week.every((count) => count === null)
            ? null
            : week.reduce((sum, count) => sum + (count || 0), 0);
          coach.callsToday = callsByCoach.get(coach.employeeId) || 0;
        });

        if (!cancelled) setRows(coaches);
      } catch (err) {
        if (!cancelled) {
          setError(err);
          setRows(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    run();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    configured,
    get,
    myEmployeeId,
    departmentsKey,
    allCoaches,
    eventTypeId,
    effectiveNow,
    reloadToken,
  ]);

  return {
    coaches: rows || [],
    loading,
    error,
    refresh,
    /** Distinguished so the screen can say which prerequisite is missing. */
    hasEmployeeProfile: Boolean(myEmployeeId),
  };
}
